/*
 * カートドロワー内の併売レコメンド（snippets/cart-drawer-recommendations.liquid）の
 * 「追加」ボタンと、バリエーション選択パネル。
 *
 * ドロワーは Section Rendering API で中身ごと差し替わるため、個々の要素にリスナーを
 * 張ると再描画のたびに失われる。この要素に対する委譲で扱い、再バインド不要にしている。
 *
 * 追加後は cart-drawer の renderContents() で差分更新する。ページのリロードは
 * ドロワーが閉じてしまうため使えない。
 */
class CartDrawerRecommendations extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.onClick.bind(this));
  }

  onClick(event) {
    const buyTrigger = event.target.closest('[data-reco-buy]');
    if (buyTrigger) {
      this.openBuyPanel(buyTrigger);
      return;
    }

    const addButton = event.target.closest('[data-reco-add]');
    if (addButton) this.addToCart(addButton);
  }

  /*
   * ポップアップは body 直下へ移してから使う。ドロワーの .drawer__inner は transform を
   * 持ち、その内側では position: fixed がドロワーに閉じ込められて画面中央に出せない。
   * 移したあとはドロワー再描画の影響を受けないので、古いものを取り除いてから移す。
   */
  detachPopup() {
    if (this.detachedPopup?.isConnected) return this.detachedPopup;

    const own = this.querySelector('.cart-reco__popup');
    if (!own) return null;

    document.querySelectorAll('body > .cart-reco__popup').forEach((stale) => stale.remove());
    document.body.appendChild(own);

    own.addEventListener('click', this.onPopupClick);
    own.addEventListener('keyup', this.onPopupKeyup);
    this.detachedPopup = own;
    return own;
  }

  onPopupClick = (event) => {
    if (
      event.target.closest('[data-reco-popup-close]') ||
      event.target.classList.contains('cart-reco__popup')
    ) {
      this.closePopup();
      return;
    }

    const addButton = event.target.closest('[data-reco-add]');
    if (addButton) this.addToCart(addButton);
  };

  onPopupKeyup = (event) => {
    if (event.code === 'Escape') this.closePopup();
  };

  openBuyPanel(trigger) {
    const popup = this.detachPopup();
    const template = trigger.closest('.cart-reco__item')?.querySelector('[data-reco-buy-tpl]');
    if (!popup || !template) return;

    popup.querySelector('[data-reco-popup-body]').replaceChildren(template.content.cloneNode(true));

    this.popupOpener = trigger;
    popup.hidden = false;

    const panel = popup.querySelector('[role="dialog"]');
    panel.scrollTop = 0;
    trapFocus(popup, panel);
  }

  closePopup() {
    const popup = this.detachedPopup;
    if (!popup || popup.hidden) return;

    popup.hidden = true;
    removeTrapFocus();
    popup.querySelector('[data-reco-popup-body]').replaceChildren();

    if (this.popupOpener) {
      this.popupOpener.focus();
      this.popupOpener = null;
    }
  }

  addToCart(button) {
    if (button.hasAttribute('disabled')) return;

    const scope = button.closest('.cart-reco__buy') || button.closest('.cart-reco__item');
    const variantId = this.resolveVariantId(button, scope);
    if (!variantId) return;

    const cart = document.querySelector('cart-drawer');
    button.setAttribute('disabled', '');
    this.showError(scope, null);

    const body = { items: [{ id: Number(variantId), quantity: 1 }] };
    if (cart) {
      body.sections = cart.getSectionsToRender().map((section) => section.id);
      body.sections_url = window.location.pathname;
    }

    fetch(`${routes.cart_add_url}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/javascript',
        'X-Requested-With': 'XMLHttpRequest',
      },
      body: JSON.stringify(body),
    })
      .then((response) => response.json())
      .then((parsed) => {
        if (parsed.status) {
          this.showError(scope, parsed.description || parsed.message);
          return;
        }

        if (!cart) {
          window.location = window.routes.cart_url;
          return;
        }

        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'cart-drawer-recommendations',
          productVariantId: variantId,
          cartData: parsed,
        });

        this.closePopup();

        const scrollTop = document.querySelector('cart-drawer-items')?.scrollTop || 0;

        cart.renderContents(parsed);
        this.restoreScroll(scrollTop);
      })
      .catch((error) => {
        console.error(error);
        this.showError(scope, window.cartStrings?.error);
      })
      .finally(() => {
        button.removeAttribute('disabled');
      });
  }

  /*
   * renderContents はドロワーの中身を丸ごと差し替えるため、スクロール位置が先頭に戻る。
   * レコメンドはカート明細の下にあり、追加した直後に先頭へ飛ぶと何が変わったのか
   * 分からなくなるので、元の位置へ戻す。
   *
   * 差し替えは 2 回起きる。1 回目はこのファイルの renderContents、2 回目は cart.js の
   * CartItems が cartUpdate を購読して ?section_id=cart-drawer を取り直すもので、
   * 通信を挟むぶん数百 ms 遅れて cart-drawer-items ごと差し替わる。
   * 2 回目を待つために監視するが、検知したらすぐ切る。つなぎっぱなしにすると、
   * その後の数量変更や明細削除まで拾って、ユーザーが動かしたスクロールを巻き戻してしまう。
   */
  restoreScroll(scrollTop) {
    if (!scrollTop) return;

    const apply = () => {
      const scroller = document.querySelector('cart-drawer-items');
      if (scroller) scroller.scrollTop = scrollTop;
    };

    apply();

    const inner = document.querySelector('#CartDrawer .drawer__inner');
    if (!inner) return;

    const observer = new MutationObserver(() => {
      apply();
      observer.disconnect();
    });
    observer.observe(inner, { childList: true });

    // 2 回目の差し替えが来なかったときに監視を残さないための保険
    setTimeout(() => observer.disconnect(), 3000);
  }

  resolveVariantId(button, scope) {
    if (button.dataset.variantId) return button.dataset.variantId;

    const select = scope?.querySelector('[data-reco-variant]');
    return select ? select.value : null;
  }

  showError(scope, message) {
    const error = scope?.querySelector('[data-reco-error]');
    if (!error) return;

    error.textContent = message || '';
    error.hidden = !message;
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
